/**
 * Template de prise de besoin : Plan de continuité TI (ITSCM) v1
 * Aligné sur ITIL 4, COBIT 2019, ISO 27001 et ISO 22301
 * Plan de continuité des services TI pour assurer la reprise après sinistre
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePlanContinuiteTiV1: IntakeTemplate = {
  id: 'plan-continuite-ti-v1',
  name: 'Prise de besoin Plan de continuité TI (ITSCM) v1',
  description:
    'Questionnaire complet pour élaborer un plan de continuité TI conforme ITIL 4, COBIT 2019, ISO 27001 et ISO 22301. Définit les stratégies de sauvegarde, restauration, sites de secours et processus de continuité.',
  documentTypeCode: 'ITSCM_PLAN',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Services Critiques',
      description: 'Informations sur l\'entreprise et identification des services critiques',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour le plan de continuité TI.',
        },
        {
          id: 'services_critiques',
          label: 'Quels sont les services TI critiques de votre organisation ?',
          type: 'textarea',
          placeholder: 'Listez les services critiques (ex: Système ERP, Email, Réseau, Serveurs de fichiers, Applications métier...)',
          required: true,
          helpText: 'ITIL 4 (ITSCM): Identification des services critiques pour priorisation.',
        },
        {
          id: 'rto_services',
          label: 'Quels sont les délais cibles de restauration (RTO) pour chaque service critique ?',
          type: 'textarea',
          placeholder: 'Ex: Système ERP: 4 heures, Email: 2 heures, Réseau: 1 heure...',
          helpText: 'ITIL 4 (ITSCM): RTO (Recovery Time Objective) pour planification.',
        },
        {
          id: 'rpo_services',
          label: 'Quels sont les objectifs de point de récupération (RPO) pour chaque service critique ?',
          type: 'textarea',
          placeholder: 'Ex: Système ERP: 1 heure, Email: 15 minutes, Données: 0 (synchronisation continue)...',
          helpText: 'ITIL 4 (ITSCM): RPO (Recovery Point Objective) pour planification.',
        },
        {
          id: 'ton_document_plan',
          label: 'Quel ton souhaitez-vous pour ce plan ?',
          type: 'select',
          options: [
            'Formel et procédural (ton impératif, étapes claires)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité d\'utilisation du plan.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Stratégies de Sauvegarde & Restauration',
      description: 'Stratégies de sauvegarde et restauration',
      icon: 'Shield',
      questions: [
        {
          id: 'strategie_sauvegarde',
          label: 'Quelle est la stratégie de sauvegarde ?',
          type: 'multiselect',
          options: [
            'Sauvegardes quotidiennes',
            'Sauvegardes horaires',
            'Sauvegardes continues (réplication)',
            'Sauvegardes incrémentielles',
            'Sauvegardes complètes',
            'Sauvegardes cloud',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4 (ITSCM): Stratégie de sauvegarde pour protection des données.',
        },
        {
          id: 'localisation_sauvegardes',
          label: 'Où sont stockées les sauvegardes ?',
          type: 'multiselect',
          options: [
            'Sur site (serveurs locaux)',
            'Hors site (datacenter distant)',
            'Cloud (Azure, AWS, etc.)',
            'Support physique (bandes, disques)',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4 (ITSCM): Localisation pour protection contre sinistres.',
        },
        {
          id: 'strategie_restauration',
          label: 'Quelle est la stratégie de restauration ?',
          type: 'select',
          options: [
            'Restauration depuis sauvegardes',
            'Réplication vers site de secours',
            'Basculement automatique',
            'Basculement manuel',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4 (ITSCM): Stratégie de restauration pour reprise rapide.',
        },
        {
          id: 'processus_restauration',
          label: 'Quel est le processus de restauration ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Évaluation → Sélection sauvegarde → Restauration → Tests → Validation → Remise en service...',
          helpText: 'ITIL 4 (ITSCM): Processus structuré pour restauration efficace.',
        },
        {
          id: 'tests_restauration',
          label: 'À quelle fréquence testez-vous la restauration ?',
          type: 'select',
          options: ['Mensuelle', 'Trimestrielle', 'Semestrielle', 'Annuelle', 'Jamais', 'À déterminer'],
          helpText: 'ITIL 4 (ITSCM): Tests réguliers essentiels pour valider la stratégie.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Sites de Secours & Infrastructure',
      description: 'Sites de secours et infrastructure de continuité',
      icon: 'Building2',
      questions: [
        {
          id: 'site_secours',
          label: 'Avez-vous un site de secours (disaster recovery site) ?',
          type: 'select',
          options: ['Oui, site dédié', 'Oui, site partagé', 'Non, mais prévu', 'Non', 'À déterminer'],
          helpText: 'ITIL 4 (ITSCM): Site de secours pour continuité en cas de sinistre.',
        },
        {
          id: 'type_site_secours',
          label: 'Quel type de site de secours avez-vous ou prévoyez-vous ?',
          type: 'select',
          options: [
            'Site chaud (hot site) - Opérationnel immédiatement',
            'Site tiède (warm site) - Partiellement configuré',
            'Site froid (cold site) - Infrastructure de base seulement',
            'Pas de site de secours',
            'À déterminer',
          ],
          helpText: 'ITIL 4 (ITSCM): Type de site selon besoins et budget.',
        },
        {
          id: 'infrastructure_secours',
          label: 'Quelle infrastructure est disponible sur le site de secours ?',
          type: 'textarea',
          placeholder: 'Décrivez: Serveurs, réseau, stockage, connectivité, etc.',
          helpText: 'ITIL 4 (ITSCM): Infrastructure pour support des services critiques.',
        },
        {
          id: 'delai_basculement',
          label: 'Quel est le délai cible de basculement vers le site de secours ?',
          type: 'text',
          placeholder: 'Ex: 2 heures pour basculement complet',
          helpText: 'ITIL 4 (ITSCM): Délai cible pour basculement efficace.',
        },
        {
          id: 'fournisseurs_secours',
          label: 'Quels fournisseurs sont impliqués dans la continuité ?',
          type: 'textarea',
          placeholder: 'Listez les fournisseurs: Cloud providers, datacenters, services managés...',
          helpText: 'ITIL 4 (ITSCM): Fournisseurs pour support de la continuité.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Processus & Tests de Continuité',
      description: 'Processus de continuité et tests',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'processus_activation',
          label: 'Quel est le processus d\'activation du plan de continuité ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Détection sinistre → Évaluation → Décision activation → Mobilisation équipe → Basculement...',
          helpText: 'ITIL 4 (ITSCM): Processus structuré pour activation efficace.',
        },
        {
          id: 'equipe_continuite',
          label: 'Qui compose l\'équipe de continuité TI ?',
          type: 'multiselect',
          options: [
            'Responsable continuité TI',
            'Administrateurs systèmes',
            'Administrateurs réseau',
            'Administrateurs bases de données',
            'Experts infrastructure',
            'Direction',
            'Autre',
          ],
          helpText: 'ITIL 4 (ITSCM): Équipe pour gestion de la continuité.',
        },
        {
          id: 'scenarios_tests',
          label: 'Quels scénarios de test sont prévus ?',
          type: 'multiselect',
          options: [
            'Test de restauration de sauvegardes',
            'Test de basculement vers site de secours',
            'Test de restauration complète',
            'Test de communication',
            'Exercice de sinistre complet',
            'Autre',
          ],
          helpText: 'ITIL 4 (ITSCM): Tests réguliers pour valider le plan.',
        },
        {
          id: 'frequence_tests',
          label: 'À quelle fréquence effectuez-vous des tests de continuité ?',
          type: 'select',
          options: ['Mensuelle', 'Trimestrielle', 'Semestrielle', 'Annuelle', 'Jamais', 'À déterminer'],
          helpText: 'ITIL 4 (ITSCM): Fréquence de tests pour maintenir la préparation.',
        },
        {
          id: 'revision_plan',
          label: 'À quelle fréquence souhaitez-vous réviser ce plan ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Après chaque test', 'À déterminer'],
          helpText: 'ITIL 4 (ITSCM): Révision régulière essentielle pour maintenir l\'efficacité.',
        },
      ],
    },
  ],
};





